import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const AnalyseSentimentAI = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'Analyse SMS Sentiment With AI' }
  ]

  const useCases = [
    {
      title: "Priority Support Queue for Negative Sentiment",
      industry: "Customer Support",
      description: "Automatically route angry or frustrated customers to senior support agents for immediate attention.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'This is ridiculous! My order still hasn't arrived!'",
        "AI analyses sentiment → Result: Negative (Angry, Confidence: 0.92)",
        "Filter: Only continue if sentiment = 'negative' AND confidence > 0.7",
        "Create urgent Zendesk ticket with priority: High",
        "Send Slack notification to @senior-support team",
        "Auto-reply: 'We're sorry! A senior agent will contact you within 15 minutes.'"
      ],
      keywords: "SMS sentiment analysis Australia, customer service automation, angry customer detection"
    },
    {
      title: "Happy Customer Review Requests",
      industry: "E-commerce & Retail",
      description: "Request reviews from satisfied customers while they're in a positive mood.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Just received my order! Love it, thank you!'",
        "AI analyses sentiment → Result: Positive (Happy, Confidence: 0.88)",
        "Filter: Only continue if sentiment = 'positive'",
        "Wait 5 minutes (gives them time to enjoy the product)",
        "Send SMS: 'So glad you love it! Mind leaving us a quick review? [link]'",
        "Track review completion in Google Sheets"
      ],
      keywords: "review request automation, positive customer feedback, SMS marketing Australia"
    },
    {
      title: "Sentiment Dashboard for Australian Businesses",
      industry: "Analytics & Reporting",
      description: "Track customer sentiment trends over time to improve products and services.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Every incoming SMS is analysed for sentiment",
        "Data is logged to Google Sheets: Date, From, Message, Sentiment, Emotion, Confidence",
        "Google Data Studio dashboard visualises trends",
        "Weekly report emailed to management",
        "Identify patterns: 'Delivery complaints spike on Fridays'",
        "Take action to improve service"
      ],
      keywords: "customer sentiment tracking, SMS analytics Australia, business intelligence"
    },
    {
      title: "VIP Customer Alert System",
      industry: "Hospitality & Luxury Retail",
      description: "Alert managers immediately when VIP customers express dissatisfaction.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "VIP customer (from premium contact group) sends SMS",
        "AI analyses sentiment → Result: Negative",
        "Immediately send SMS to store manager: '⚠️ VIP Jane Smith unhappy: [message]'",
        "Create task in Asana for manager follow-up",
        "Log interaction in HubSpot CRM",
        "Auto-reply offering personal manager callback"
      ],
      keywords: "VIP customer management, luxury retail SMS, personalised service Australia"
    },
    {
      title: "Product Feedback Categorisation",
      industry: "Product Development",
      description: "Automatically categorise product feedback by sentiment to prioritise improvements.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends product feedback via SMS",
        "AI analyses sentiment and categorises intent",
        "Positive feedback → Add to testimonials database (Airtable)",
        "Negative feedback → Create product improvement task (Trello)",
        "Neutral feedback → Log for later review (Google Sheets)",
        "Product team receives weekly summary report"
      ],
      keywords: "product feedback analysis, customer insights Australia, SMS feedback collection"
    }
  ]

  const faqItems = [
    {
      question: "How accurate is the AI sentiment analysis for Australian English?",
      answer: "Our AI is trained on diverse English variants including Australian English. It achieves 85-92% accuracy in sentiment detection and understands Australian slang, idioms, and colloquialisms. The confidence score helps you filter out uncertain results."
    },
    {
      question: "What sentiments can the AI detect in SMS messages?",
      answer: "The AI classifies messages into three main sentiments: Positive (happy, satisfied, grateful), Negative (angry, frustrated, disappointed), and Neutral (informational, factual). It also provides emotion tags like 'angry', 'worried', 'excited', and tone indicators like 'urgent' or 'professional'."
    },
    {
      question: "Can I use sentiment analysis for SMS marketing campaigns in Australia?",
      answer: "Yes! Sentiment analysis is perfect for Australian SMS marketing. You can identify unhappy customers for retention campaigns, find promoters for referral programs, or segment audiences based on their emotional response to your messages."
    },
    {
      question: "What's the confidence score and how should I use it?",
      answer: "The confidence score (0.0 to 1.0) indicates how certain the AI is about the sentiment. We recommend only acting on results with confidence > 0.7. Low confidence (<0.6) should be routed to human review to avoid misclassification."
    },
    {
      question: "Does sentiment analysis work with SMS messages in different formats?",
      answer: "Yes! The AI works with standard SMS messages, long messages (concatenated), messages with emojis, abbreviations, and even messages with poor grammar or typos. It's designed to understand real-world customer communication."
    },
    {
      question: "How fast is the sentiment analysis?",
      answer: "Analysis is near-instant, typically completing within 1-2 seconds. Since DataFlows SMS uses instant webhook triggers (not polling), the entire workflow from receiving an SMS to analysing sentiment and taking action happens in under 5 seconds."
    },
    {
      question: "Can I analyse sentiment for bulk SMS responses in Australia?",
      answer: "Absolutely! Whether you receive 10 or 10,000 SMS responses to your Australian SMS campaign, each message is analysed individually in real-time. This is perfect for event feedback, product launches, or survey responses."
    },
    {
      question: "What happens if the AI can't determine sentiment?",
      answer: "If the AI confidence is very low or the message is ambiguous, you can set up a fallback workflow. Common approaches: route to human review, mark as 'neutral', or request clarification from the sender via automated SMS response."
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>Analyse SMS Sentiment With AI - DataFlows Zapier | SMS Sentiment Analysis Australia</title>
        <meta
          name="description"
          content="Automate SMS sentiment analysis for Australian businesses with DataFlows and Zapier. Detect angry customers, identify happy customers for reviews, and track sentiment trends. AI-powered, instant webhooks, 85-92% accuracy."
        />
        <meta name="keywords" content="SMS sentiment analysis Australia, AI sentiment detection, SMS automation Australia, customer sentiment tracking, SMS API Australia, Zapier SMS sentiment, angry customer detection, review request automation" />
        <meta property="og:title" content="AI SMS Sentiment Analysis for Australian Businesses - DataFlows Zapier" />
        <meta property="og:description" content="Automatically analyse customer sentiment in SMS messages. Route angry customers to priority support, request reviews from happy customers, track trends." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/actions/analyse-sentiment-ai')} />
      </Helmet>

      <div className="feature-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="feature-hero" data-aos="fade-up">
        <div className="container">
          <div className="hero-badge">AI-Powered Action</div>
          <h1>Analyse SMS Sentiment With AI</h1>
          <p className="hero-subtitle">
            Automatically detect customer emotions in SMS messages. Route angry customers to priority support, identify happy customers for review requests, and track sentiment trends across your Australian business.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">85-92%</div>
              <div className="stat-label">Accuracy Rate</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">&lt;2s</div>
              <div className="stat-label">Analysis Time</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">3</div>
              <div className="stat-label">Sentiment Types</div>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-overview" data-aos="fade-up">
        <div className="container">
          <h2>What Is SMS Sentiment Analysis?</h2>
          <p className="section-intro">
            SMS sentiment analysis uses advanced AI to automatically understand the emotion behind customer text messages. Instead of manually reading and categorising every SMS, our AI instantly detects whether a customer is happy, angry, or neutral—helping Australian businesses respond faster and more appropriately.
          </p>

          <div className="overview-grid">
            <div className="overview-card">
              <div className="card-icon">😊</div>
              <h3>Positive Sentiment</h3>
              <p>Detects happy, satisfied, and grateful customers. Perfect for requesting reviews, upselling products, or gathering testimonials.</p>
              <div className="emotion-tags">
                <span>Happy</span>
                <span>Excited</span>
                <span>Grateful</span>
                <span>Satisfied</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">😠</div>
              <h3>Negative Sentiment</h3>
              <p>Identifies angry, frustrated, or disappointed customers. Route to senior support, offer compensation, or escalate immediately.</p>
              <div className="emotion-tags negative">
                <span>Angry</span>
                <span>Frustrated</span>
                <span>Disappointed</span>
                <span>Worried</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">😐</div>
              <h3>Neutral Sentiment</h3>
              <p>Detects informational, factual messages with no strong emotion. Handle with standard workflows and response times.</p>
              <div className="emotion-tags neutral">
                <span>Informational</span>
                <span>Enquiry</span>
                <span>Factual</span>
                <span>Professional</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works" data-aos="fade-up">
        <div className="container">
          <h2>How Sentiment Analysis Works in Zapier</h2>
          <p className="section-intro">
            DataFlows SMS integrates with Zapier to provide instant, AI-powered sentiment analysis for every SMS message your Australian business receives.
          </p>

          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Customer Sends SMS</h3>
                <p>A customer texts your Australian business number with feedback, a question, or a complaint.</p>
                <div className="code-example">
                  From: +61412345678<br/>
                  To: +61400123456<br/>
                  Message: "This is ridiculous! My order still hasn't arrived after 2 weeks!"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>DataFlows Receives SMS</h3>
                <p>DataFlows SMS platform receives the message and instantly triggers your Zapier workflow via webhook (no polling delay).</p>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>AI Analyses Sentiment</h3>
                <p>The "Analyse SMS Sentiment With AI" action processes the message and returns detailed results.</p>
                <div className="code-example">
                  sentiment: "negative"<br/>
                  emotion: "angry"<br/>
                  confidence: 0.92<br/>
                  tone: "urgent"<br/>
                  reasoning: "Customer expresses frustration about delayed order"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Take Action Based on Sentiment</h3>
                <p>Use Zapier filters and paths to route the conversation appropriately.</p>
                <ul>
                  <li>Negative → Create urgent support ticket</li>
                  <li>Positive → Request review or testimonial</li>
                  <li>Neutral → Standard response workflow</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="use-cases-section" data-aos="fade-up">
        <div className="container">
          <h2>Real-World Use Cases for Australian Businesses</h2>
          <p className="section-intro">
            See how Australian businesses use SMS sentiment analysis to improve customer satisfaction, increase reviews, and identify issues before they escalate.
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-detailed" data-aos="fade-up">
              <div className="use-case-header">
                <div>
                  <h3>{useCase.title}</h3>
                  <span className="industry-tag">{useCase.industry}</span>
                </div>
              </div>
              <p className="use-case-description">{useCase.description}</p>

              <div className="workflow-breakdown">
                <h4>Workflow Steps:</h4>
                <ol>
                  {useCase.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>

            </div>
          ))}
        </div>
      </section>

      <section className="australian-focus" data-aos="fade-up">
        <div className="container">
          <h2>Why Australian Businesses Choose DataFlows SMS Sentiment Analysis</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>🇦🇺 Understands Australian English</h3>
              <p>Trained on Australian slang, idioms, and communication styles. Understands "mate", "no worries", and other Aussie expressions.</p>
            </div>
            <div className="benefit-item">
              <h3>⚡ Instant Webhooks</h3>
              <p>Real-time analysis with no polling delays. SMS received → Analysed → Action taken in under 5 seconds.</p>
            </div>
            <div className="benefit-item">
              <h3>📊 High Accuracy</h3>
              <p>85-92% accuracy rate with confidence scores. Filter by confidence to ensure reliable results.</p>
            </div>
            <div className="benefit-item">
              <h3>🔒 ACMA Compliant</h3>
              <p>Fully compliant with Australian SMS regulations and privacy laws (Privacy Act 1988).</p>
            </div>
            <div className="benefit-item">
              <h3>💰 Cost-Effective</h3>
              <p>No per-analysis charges. Included with your DataFlows SMS plan. Perfect for Australian small businesses.</p>
            </div>
            <div className="benefit-item">
              <h3>🔗 6,000+ App Integrations</h3>
              <p>Connect with Zendesk, Slack, HubSpot, Google Sheets, and thousands more via Zapier.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="setup-guide" data-aos="fade-up">
        <div className="container">
          <h2>How to Set Up SMS Sentiment Analysis in Zapier</h2>

          <div className="setup-step">
            <h3>Step 1: Create Your Zap</h3>
            <ol>
              <li>Log in to <a href="https://zapier.com" target="_blank" rel="noopener noreferrer">Zapier</a></li>
              <li>Click "Create Zap"</li>
              <li>Search for "DataFlows SMS" and select it as your trigger app</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 2: Choose Your Trigger</h3>
            <ol>
              <li>Select trigger event: <strong>"New SMS Received"</strong></li>
              <li>Connect your DataFlows SMS account using your API token</li>
              <li>Get your API token from: <a href="https://sms.dataflows.com.au/developers/api" target="_blank" rel="noopener noreferrer">https://sms.dataflows.com.au/developers/api</a></li>
              <li>Test the trigger with a sample SMS</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 3: Add Sentiment Analysis Action</h3>
            <ol>
              <li>Click "+" to add an action</li>
              <li>Search for "DataFlows SMS" and select it</li>
              <li>Choose action event: <strong>"Analyse SMS Sentiment With AI"</strong></li>
              <li>Map the "Message" field to the incoming SMS content from Step 2</li>
              <li>Test the action to see sentiment results</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 4: Route Based on Sentiment</h3>
            <ol>
              <li>Add a <strong>Filter</strong> or <strong>Paths</strong> step</li>
              <li>Create different workflows for each sentiment:
                <ul>
                  <li><strong>Path 1 (Negative):</strong> sentiment = "negative" AND confidence &gt; 0.7</li>
                  <li><strong>Path 2 (Positive):</strong> sentiment = "positive"</li>
                  <li><strong>Path 3 (Neutral):</strong> sentiment = "neutral"</li>
                </ul>
              </li>
              <li>For each path, add appropriate actions (create ticket, send email, log to sheet, etc.)</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 5: Test and Activate</h3>
            <ol>
              <li>Send test SMS messages with different sentiments</li>
              <li>Verify each workflow path executes correctly</li>
              <li>Check confidence scores are within acceptable range</li>
              <li>Turn on your Zap to start automating!</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="faq-section" data-aos="fade-up">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <h2>Ready to Start Analysing SMS Sentiment?</h2>
          <p>Connect DataFlows SMS with Zapier and start understanding your customers' emotions automatically.</p>
          <div className="cta-buttons">
            <a href="https://zapier.com/apps/dataflows-sms" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              Get Started with Zapier
            </a>
            <Link to="/integrations/zapier" className="btn btn-secondary">
              View All Zapier Features
            </Link>
          </div>
        </div>
      </section>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default AnalyseSentimentAI
